<script setup lang="ts">
definePageMeta({
  title: 'Privacy Policy - Collor',
  hideChrome: true,
})
</script>

<template>
  <UPageSection>
    <div class="min-h-[60vh] flex items-center justify-center px-8 py-10">
      <div
          class="max-w-3xl w-full rounded-2xl border border-white/10 bg-bush-900/60 backdrop-blur-xl shadow-2xl px-8 py-10 md:p-10 text-[#cedec2]"
      >
        <h1 class="text-2xl md:text-3xl font-semibold mb-6 text-golden-grass">
          Privacy Policy
        </h1>

        <p class="text-sm mb-6 opacity-80">
          This Privacy Policy explains how Collor BV (&ldquo;Collor&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
          &ldquo;our&rdquo;) collects, uses and protects personal data in the context of our IoT solutions and
          related services.
        </p>

        <div class="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              1. Controller
            </h2>
            <p>
              Collor BV<br />
              Beverlosesteenweg&nbsp;129<br />
              3583&nbsp;Beringen, Belgium<br />
              BTW&nbsp;BE0801581076<br />
              Email:
              <a
                  href="mailto:info@collor.be"
                  class="text-golden-grass hover:text-golden-grass/80 hover:underline"
              >
                info@collor.be
              </a>
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              2. Personal data we process
            </h2>
            <p class="mb-2">
              Depending on how you interact with us, we may process the following categories of personal data:
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li>Contact details (such as name, email address, phone number, organisation).</li>
              <li>Professional information (such as role, department, practice or municipality).</li>
              <li>Account and usage data related to our platforms and dashboards.</li>
              <li>Technical data (such as IP address, browser type and basic device information).</li>
              <li>Communication data (such as emails or messages you send us).</li>
            </ul>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              3. Purposes and legal bases
            </h2>
            <p class="mb-2">
              We process personal data only when we have a valid legal basis under applicable data protection law,
              including:
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li>
                To respond to enquiries, provide support and manage our relationship with you
                (legitimate interest or contract performance).
              </li>
              <li>
                To set up and manage user accounts and access to our IoT platforms
                (contract performance).
              </li>
              <li>
                To maintain, secure and improve our services and infrastructure
                (legitimate interest).
              </li>
              <li>
                To comply with legal and regulatory obligations.
              </li>
              <li>
                Where required, based on your consent (for example, for certain communications).
              </li>
            </ul>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              4. Data sharing and processors
            </h2>
            <p>
              We may share personal data with trusted service providers who process data on our behalf, for example
              for hosting, security, analytics or communications. These parties are bound by contractual obligations
              to protect your data and only process it according to our instructions.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              5. International transfers
            </h2>
            <p>
              Where personal data is transferred outside the European Economic Area, we take appropriate measures to
              ensure an adequate level of protection, for example by using standard contractual clauses or other
              recognised safeguards.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              6. Data retention
            </h2>
            <p>
              We retain personal data only for as long as necessary for the purposes described above or as required
              by law. Retention periods may vary depending on the type of data and the context in which it was
              collected.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              7. Your rights
            </h2>
            <p class="mb-2">
              Subject to applicable law, you may have the right to:
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li>Request access to your personal data.</li>
              <li>Request correction of inaccurate or incomplete data.</li>
              <li>Request erasure of your data in certain circumstances.</li>
              <li>Request restriction of processing.</li>
              <li>Object to certain types of processing.</li>
              <li>Request data portability where applicable.</li>
            </ul>
            <p class="mt-2">
              To exercise your rights, please contact us at
              <a
                  href="mailto:info@collor.be"
                  class="text-golden-grass hover:text-golden-grass/80 hover:underline"
              >
                info@collor.be
              </a>.
              You also have the right to lodge a complaint with your local supervisory authority.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              8. Updates to this policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time, for example to reflect changes in our services or
              in applicable law. The version available on this website is always the most recent version. We
              encourage you to review it periodically.
            </p>
          </section>
        </div>
      </div>
    </div>
  </UPageSection>
</template>
